import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Button, Badge, Modal } from "react-bootstrap";
import { TbTrash, TbPlus } from "react-icons/tb";

import "./SensorAnalysis.css";
import { sensorAnalysisApi } from "../../services/sensorAnalysisApi";
import { addToast } from "../../redux/toast";
import {
  setSensorAnalysisSubmitting,
  setSensorAnalysisError,
  isResourceInState,
  applyActionToSensorAnalysisState,
  setSensorAnalysisState,
  setPreRayConfigurations,
} from "../../redux/sensorAnalysis";
import { clearWindowFilters } from "../../redux/windowData";
import { useWindowSelection } from "../objectSelection/useObjectSelection";
import {
  clearSelections,
  toggleSelectionMode,
  isSelectionModeActive,
} from "../../redux/objectSelection";
import { reduxLocalStorage } from "../utility/storage";
import { getHasActiveFilters } from "../CustomHooks";
import {
  resources,
  actions,
  sensorAnalysisDefaultActiveTab,
  statesNames,
} from "../../redux/sensorAnalysisConstants";
import ObjectSelectionConfirmationBox from "../general/ObjectSelectionConfirmationBox";
import {
  ExecutionModal,
  ExecutionNavigation,
  ExecutionContent,
  ExecutionForm,
  ExecutionTable,
  ExecutionActions,
} from "../general/ExecutionModal";

const DEFAULT_PRE_RT_CONFIG = {
  meshingMethod: "automatic",
  squareMeterPerSensor: 1,
  minDistanceFromSides: 0,
  distanceBetweenSensors: 1,
  horizontal: 1,
  vertical: 1,
  sensorOffset: 0.5,
  processingInterval: 15,
};

const PreRTConfigStep = ({
  config,
  setConfig,
  onNext,
  onNextText,
  onExecuteAll,
  useAllWindows,
  setUseAllWindows,
  showUseAllOption,
}) => {
  const showSquareMeterPerSensor =
    config.meshingMethod === "automatic" || config.meshingMethod === "grid";
  const showMinDistanceFromSides =
    config.meshingMethod === "grid" || config.meshingMethod === "area";
  const showDistanceBetweenSensors = config.meshingMethod === "area";
  const showHorizontalVertical = config.meshingMethod === "amount";

  return (
    <ExecutionForm>
      <h4>Pre Ray Tracing Configuration</h4>
      <div className="pv-form-grid">
        <Form.Group className="pv-field">
          <Form.Label>Meshing Method</Form.Label>
          <Form.Select
            value={config.meshingMethod}
            onChange={(e) =>
              setConfig({ ...config, meshingMethod: e.target.value })
            }
          >
            <option value="amount">Amount</option>
            <option value="area">Area</option>
            <option value="automatic">Automatic (Amount/Area)</option>
            <option value="grid">Grid</option>
          </Form.Select>
        </Form.Group>

        {showSquareMeterPerSensor && (
          <Form.Group className="pv-field">
            <Form.Label>Square Meter Per Sensor</Form.Label>
            <Form.Control
              type="number"
              step="0.1"
              value={config.squareMeterPerSensor}
              onChange={(e) =>
                setConfig({
                  ...config,
                  squareMeterPerSensor: Number(e.target.value),
                })
              }
            />
          </Form.Group>
        )}

        {showMinDistanceFromSides && (
          <Form.Group className="pv-field">
            <Form.Label>Min Distance From Sides (% of Area)</Form.Label>
            <Form.Control
              type="number"
              step="0.1"
              value={config.minDistanceFromSides}
              onChange={(e) =>
                setConfig({
                  ...config,
                  minDistanceFromSides: Number(e.target.value),
                })
              }
            />
          </Form.Group>
        )}

        {showDistanceBetweenSensors && (
          <Form.Group className="pv-field">
            <Form.Label>Distance Between Sensors</Form.Label>
            <Form.Control
              type="number"
              step="0.1"
              value={config.distanceBetweenSensors}
              onChange={(e) =>
                setConfig({
                  ...config,
                  distanceBetweenSensors: Number(e.target.value),
                })
              }
            />
          </Form.Group>
        )}

        {showHorizontalVertical && (
          <>
            <Form.Group className="pv-field">
              <Form.Label>Horizontal</Form.Label>
              <Form.Control
                type="number"
                step="1"
                value={config.horizontal}
                onChange={(e) =>
                  setConfig({ ...config, horizontal: Number(e.target.value) })
                }
              />
            </Form.Group>

            <Form.Group className="pv-field">
              <Form.Label>Vertical</Form.Label>
              <Form.Control
                type="number"
                step="1"
                value={config.vertical}
                onChange={(e) =>
                  setConfig({ ...config, vertical: Number(e.target.value) })
                }
              />
            </Form.Group>
          </>
        )}
      </div>

      <div className="pv-form-grid-2">
        <Form.Group className="pv-field">
          <Form.Label>Sensor Offset</Form.Label>
          <Form.Control
            type="number"
            step="0.1"
            value={config.sensorOffset}
            onChange={(e) =>
              setConfig({ ...config, sensorOffset: Number(e.target.value) })
            }
          />
        </Form.Group>

        <Form.Group className="pv-field">
          <Form.Label>Processing Interval</Form.Label>
          <Form.Control
            type="number"
            step="1"
            value={config.processingInterval}
            onChange={(e) =>
              setConfig({
                ...config,
                processingInterval: Number(e.target.value),
              })
            }
          />
        </Form.Group>
      </div>

      {showUseAllOption && (
        <div className="mesh-all-option">
          <Form.Check
            type="checkbox"
            id="mesh-all-windows"
            label="Do meshing for all windows"
            checked={useAllWindows}
            onChange={(e) => setUseAllWindows(e.target.checked)}
            className="mesh-all-checkbox"
          />
        </div>
      )}

      <ExecutionActions align="center">
        {useAllWindows ? (
          <Button
            variant="success"
            onClick={onExecuteAll}
            className="pv-ui-submit-btn"
          >
            Execute
          </Button>
        ) : (
          <Button variant="primary" onClick={onNext} className="pv-ui-primary">
            {onNextText}
          </Button>
        )}
      </ExecutionActions>
    </ExecutionForm>
  );
};

const DEFAULT_RT_CONFIG = {
  consierTrain: false,
  considerVegetation: true,
  storeSkyline: true,
  plotSkyline: true,
  numberOfSkylines: 2,
};

const RTConfigStep = ({
  config,
  setConfig,
  onNext,
  onExecuteAll,
  useAllWindows,
  setUseAllWindows,
  areWindowsSelected,
  showUseAllOption,
}) => {
  return (
    <ExecutionForm>
      <h4>Ray Tracing Configuration</h4>
      <div className="pv-form-grid">
        <Form.Group className="pv-field">
          <Form.Label>Consider Train</Form.Label>
          <Form.Select
            value={config.considerTrain}
            onChange={(e) =>
              setConfig({ ...config, considerTrain: e.target.value })
            }
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="pv-field">
          <Form.Label>Consider Vegetation</Form.Label>
          <Form.Select
            value={config.considerVegetation}
            onChange={(e) =>
              setConfig({ ...config, considerVegetation: e.target.value })
            }
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="pv-field">
          <Form.Label>Store Skyline</Form.Label>
          <Form.Select
            value={config.storeSkyline}
            onChange={(e) =>
              setConfig({ ...config, storeSkyline: e.target.value })
            }
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="pv-field">
          <Form.Label>Plot Skyline</Form.Label>
          <Form.Select
            value={config.plotSkyline}
            onChange={(e) =>
              setConfig({ ...config, plotSkyline: e.target.value })
            }
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="pv-field">
          <Form.Label>Number of Skylines</Form.Label>
          <Form.Select
            value={config.numberOfSkylines}
            onChange={(e) =>
              setConfig({ ...config, numberOfSkylines: Number(e.target.value) })
            }
          >
            <option value="1">1</option>
            <option value="2">2</option>
          </Form.Select>
        </Form.Group>
      </div>

      {showUseAllOption && (
        <div className="mesh-all-option">
          <Form.Check
            type="checkbox"
            id="mesh-all-windows"
            label="Do ray tracing for all windows"
            checked={useAllWindows}
            onChange={(e) => setUseAllWindows(e.target.checked)}
            className="mesh-all-checkbox"
          />
        </div>
      )}

      <ExecutionActions align="center">
        {areWindowsSelected ? (
          <Button
            variant="success"
            onClick={onExecuteAll}
            className="pv-ui-submit-btn"
          >
            Execute
          </Button>
        ) : (
          <Button variant="primary" onClick={onNext} className="pv-ui-primary">
            Select Windows
          </Button>
        )}
      </ExecutionActions>
    </ExecutionForm>
  );
};

const WindowSelectionConfirmationBox = ({ allWindows, onConfirm }) => {
  const filteredWindows = useSelector(
    (state) => state.windowData.filteredWindows
  );
  const windowFilters = useSelector((state) => state.windowData.filters);
  const hasActiveFilters = getHasActiveFilters(windowFilters);

  const availableWindows = hasActiveFilters ? filteredWindows : allWindows;

  const { selectedWindowsData } = useWindowSelection(availableWindows);

  return (
    <ObjectSelectionConfirmationBox
      selectedObjects={selectedWindowsData}
      onConfirm={() => onConfirm(selectedWindowsData)}
    />
  );
};

const ConfigurationRecord = ({ config, windows, onDelete }) => {
  const configData = config.config;
  const getConfigDisplayValues = () => {
    const values = [];
    values.push(`Method: ${configData.meshingMethod}`);

    if (
      configData.meshingMethod === "automatic" ||
      configData.meshingMethod === "grid"
    ) {
      values.push(`Area per sensor: ${configData.squareMeterPerSensor}m²`);
    }

    if (
      configData.meshingMethod === "grid" ||
      configData.meshingMethod === "area"
    ) {
      values.push(`Min distance: ${configData.minDistanceFromSides}%`);
    }

    if (configData.meshingMethod === "area") {
      values.push(`Sensor distance: ${configData.distanceBetweenSensors}m`);
    }

    if (configData.meshingMethod === "amount") {
      values.push(`Grid: ${configData.horizontal}×${configData.vertical}`);
    }

    values.push(`Offset: ${configData.sensorOffset}m`);
    values.push(`Interval: ${configData.processingInterval}min`);

    return values;
  };

  return (
    <div className="config-record">
      <div className="record-header">
        <h6 style={{ display: "flex", alignItems: "center", gap: 30 }}>
          <span>Configuration {config.id}</span>
          <span className="window-summary">
            {windows.length} windows selected
          </span>
        </h6>
        <div className="record-actions">
          <Button size="md" variant="outline-danger" onClick={onDelete}>
            <TbTrash />
          </Button>
        </div>
      </div>
      <div className="record-details">
        <div className="config-summary">
          <div className="config-values">
            {getConfigDisplayValues().map((value, index) => (
              <Badge
                pill
                bg="primary"
                key={index}
                style={{
                  fontWeight: 400,
                  padding: "6px 12px",
                  fontSize: "12px",
                }}
              >
                {value}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const EmptyMessage = ({ message, onButtonClick, buttonText }) => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <p>{message}</p>
      <button onClick={onButtonClick} className="pv-ui-primary">
        {buttonText}
      </button>
    </div>
  );
};

const tabItems = [
  { key: statesNames.HISTORY.ROOT, label: "History" },
  { key: statesNames.PRE_RAY.CONFIG, label: "Pre Ray Tracing" },
  { key: statesNames.RAY.CONFIG, label: "Ray Tracing" },
  { key: statesNames.MANUAL_MESHING.ROOT, label: "Manual Meshing" },
];

const SensorAnalysisManager = () => {
  const dispatch = useDispatch();
  const { ui, sensorAnalysisState } = useSelector((s) => s.sensorAnalysis);
  const allWindows = useSelector((state) => state.windowData.allWindows);
  const isSelectionActive = useSelector((state) =>
    isSelectionModeActive(state)
  );
  const projectId = reduxLocalStorage.getItem("project_id");
  const token = localStorage.getItem("token");

  const [activeTab, setActiveTabState] = useState(
    sensorAnalysisDefaultActiveTab
  );
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    dispatch(setSensorAnalysisState(tab));
  };

  useEffect(() => {
    if (tabItems?.map((t) => t.key).includes(sensorAnalysisState)) {
      setActiveTabState(sensorAnalysisState);

      if (sensorAnalysisState === statesNames.RAY.CONFIG) {
        setCurrentConfig({ ...DEFAULT_RT_CONFIG });
      } else if (sensorAnalysisState === statesNames.PRE_RAY.CONFIG) {
        setCurrentConfig({ ...DEFAULT_PRE_RT_CONFIG });
      }
    }
  }, [sensorAnalysisState]);

  const [requests, setRequests] = useState([]);

  const [useAllWindows, setUseAllWindows] = useState(false);
  const [currentConfig, setCurrentConfig] = useState({
    ...DEFAULT_PRE_RT_CONFIG,
  });

  const { selectedWindowsData: selectedWindows } = useWindowSelection();

  const areWindowsSelected = selectedWindows.length > 0;

  const onNextText = useMemo(() => {
    if (areWindowsSelected) {
      return `Submit ${selectedWindows.length} windows`;
    }
    return "Next";
  }, [areWindowsSelected, selectedWindows.length]);

  const configurations = useSelector(
    (state) => state.sensorAnalysis.preRay.configurations
  );
  const setConfigurations = useCallback(
    (configurations) => {
      dispatch(setPreRayConfigurations(configurations));
    },
    [dispatch]
  );
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);

  const isWindowSelectionConfirmationBoxOpen = isResourceInState(
    sensorAnalysisState,
    resources.WINDOW_SELECTION_CONFIRMATION_BOX
  );

  const isNarrowMode = isWindowSelectionConfirmationBoxOpen;

  const handleAddConfiguration = useCallback(() => {
    const newConfiguration = {
      id: configurations.length + 1,
      config: { ...currentConfig },
      windows: [...selectedWindows],
      createdAt: new Date().toISOString(),
    };

    setConfigurations([...configurations, newConfiguration]);
  }, [currentConfig, selectedWindows, configurations, setConfigurations]);

  const handleNextToSelection = () => {
    dispatch(toggleSelectionMode());

    if (areWindowsSelected) {
      handleAddConfiguration();
      dispatch(applyActionToSensorAnalysisState(actions.ON_NEXT));
    } else {
      dispatch(
        applyActionToSensorAnalysisState(actions.ON_GO_TO_SELECT_WINDOWS)
      );
    }
  };

  const handleRTNextToSelection = () => {
    dispatch(toggleSelectionMode());
    dispatch(applyActionToSensorAnalysisState(actions.ON_GO_TO_SELECT_WINDOWS));
  };

  const handleConfirmWindowSelection = () => {
    handleAddConfiguration();
    dispatch(applyActionToSensorAnalysisState(actions.ON_SUBMIT));
  };

  const handleAddAnotherConfiguration = () => {
    setCurrentConfig({ ...DEFAULT_PRE_RT_CONFIG });
    dispatch(clearSelections());
    dispatch(applyActionToSensorAnalysisState(actions.ON_ADD));
  };

  const handleDeleteConfiguration = (configIndex) => {
    setConfigToDelete(configIndex);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteConfiguration = () => {
    if (configToDelete !== null) {
      setConfigurations(
        configurations.filter((_, index) => index !== configToDelete)
      );
      setConfigToDelete(null);
    }
    setShowDeleteConfirmation(false);
  };

  const cancelDeleteConfiguration = () => {
    setConfigToDelete(null);
    setShowDeleteConfirmation(false);
  };

  const handleExecuteAll = async () => {
    try {
      dispatch(setSensorAnalysisSubmitting(true));

      dispatch(
        addToast({
          id: Math.random().toString(36).slice(2),
          type: "success",
          message: "Sensor analysis submitted for all windows",
        })
      );

      setActiveTab(statesNames.HISTORY.ROOT);
      setConfigurations([]);
      setCurrentConfig({ ...DEFAULT_PRE_RT_CONFIG });
      dispatch(clearSelections());

      const newRequest = {
        id: Math.random().toString(36).slice(2),
        name: `Sensor Analysis (All Windows) ${new Date().toLocaleString()}`,
        createdAt: new Date().toISOString(),
        status: "submitted",
        configurations: 1,
      };
      setRequests((prev) => [newRequest, ...prev]);
    } catch (e) {
      dispatch(setSensorAnalysisError("Failed to submit sensor analysis"));
      dispatch(
        addToast({
          id: Math.random().toString(36).slice(2),
          type: "error",
          message: "Failed to submit sensor analysis",
        })
      );
    } finally {
      dispatch(setSensorAnalysisSubmitting(false));
    }
  };

  const handleSubmitAll = async () => {
    if (configurations.length === 0) {
      dispatch(
        addToast({
          id: Math.random().toString(36).slice(2),
          type: "warning",
          message: "Please create at least one configuration before submitting",
        })
      );
      return;
    }

    try {
      dispatch(setSensorAnalysisSubmitting(true));

      const requestData = configurations.map((config) => ({
        configurationParams: config.config,
        windowIds: config.windows.map((w) => w.id),
        batchName: `Configuration ${config.id}`,
      }));

      await sensorAnalysisApi.createRequest({
        projectId,
        token,
        name: `Sensor Analysis ${new Date().toLocaleString()}`,
        configurations: requestData,
      });

      dispatch(
        addToast({
          id: Math.random().toString(36).slice(2),
          type: "success",
          message: `Sensor analysis submitted with ${configurations.length} configurations`,
        })
      );

      dispatch(applyActionToSensorAnalysisState(actions.ON_SUBMIT));
      setConfigurations([]);
      setCurrentConfig({ ...DEFAULT_PRE_RT_CONFIG });
      dispatch(clearSelections());

      const newRequest = {
        id: Math.random().toString(36).slice(2),
        name: `Sensor Analysis ${new Date().toLocaleString()}`,
        createdAt: new Date().toISOString(),
        status: "submitted",
        configurations: configurations.length,
      };
      setRequests((prev) => [newRequest, ...prev]);
    } catch (e) {
      dispatch(setSensorAnalysisError("Failed to submit sensor analysis"));
      dispatch(
        addToast({
          id: Math.random().toString(36).slice(2),
          type: "error",
          message: "Failed to submit sensor analysis",
        })
      );
    } finally {
      dispatch(setSensorAnalysisSubmitting(false));
    }
  };

  const handleClose = () => {
    dispatch(clearSelections());

    if (isSelectionActive) {
      dispatch(toggleSelectionMode());
    }

    dispatch(clearWindowFilters());

    setCurrentConfig({ ...DEFAULT_PRE_RT_CONFIG });
    dispatch(clearSelections());
    setConfigurations([]);

    dispatch(applyActionToSensorAnalysisState(actions.ON_CLOSE));
  };

  const formatDateTime = (d) => {
    try {
      const dt = typeof d === "string" ? new Date(d) : d;
      return dt.toLocaleString();
    } catch {
      return String(d);
    }
  };

  const historyColumns = [
    { key: "name", label: "Name" },
    {
      key: "createdAt",
      label: "Date",
      render: (r) => formatDateTime(r.createdAt),
    },
    {
      key: "status",
      label: "Status",
      render: (r) => (
        <span
          className={`pv-chip ${
            r.status ? `pv-chip--status-${String(r.status)}` : ""
          }`}
        >
          {r.status || "-"}
        </span>
      ),
    },
    { key: "configurations", label: "# of Configurations" },
  ];

  const isOpen = isResourceInState(
    sensorAnalysisState,
    resources.SENSOR_ANALYSIS_CONTRAINER
  );

  return (
    <>
      <ExecutionModal
        isOpen={isOpen}
        onClose={handleClose}
        title="Sensor Analysis"
        size={isNarrowMode ? "auto" : "large"}
        className={`${isNarrowMode ? "narrow-mode" : ""}`}
        noOverlay={isNarrowMode}
      >
        <ExecutionNavigation
          items={tabItems}
          activeItem={activeTab}
          onItemClick={setActiveTab}
        />
        {isResourceInState(sensorAnalysisState, resources.HISTORY_TAB) && (
          <ExecutionContent style={{ height: "65vh" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <h4 style={{ margin: 0, color: "#111827" }}>History</h4>
            </div>

            <ExecutionTable
              columns={historyColumns}
              data={requests}
              emptyMessage={
                <EmptyMessage
                  message="No requests yet. Create a new request to get started."
                  onButtonClick={() =>
                    dispatch(
                      applyActionToSensorAnalysisState(actions.ON_NEW_REQUEST)
                    )
                  }
                  buttonText="+Create new request"
                />
              }
            />
          </ExecutionContent>
        )}

        <ExecutionContent
          style={isNarrowMode ? { height: "auto" } : { height: "65vh" }}
        >
          {isResourceInState(sensorAnalysisState, resources.CONFIG_TAB) && (
            <PreRTConfigStep
              config={currentConfig}
              setConfig={setCurrentConfig}
              onNext={handleNextToSelection}
              onNextText={onNextText}
              onExecuteAll={handleExecuteAll}
              useAllWindows={useAllWindows}
              setUseAllWindows={setUseAllWindows}
              showUseAllOption={!areWindowsSelected}
            />
          )}

          {isResourceInState(sensorAnalysisState, resources.RT_CONFIG_TAB) && (
            <RTConfigStep
              config={currentConfig}
              setConfig={setCurrentConfig}
              onNext={handleRTNextToSelection}
              onExecuteAll={handleExecuteAll}
              useAllWindows={useAllWindows}
              setUseAllWindows={setUseAllWindows}
              areWindowsSelected={areWindowsSelected || useAllWindows}
              showUseAllOption={!areWindowsSelected}
            />
          )}

          {isResourceInState(sensorAnalysisState, resources.RECORDS_TAB) && (
            <div className="pv-card">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <h4 style={{ margin: 0, color: "#111827" }}>
                  Configuration Summary
                </h4>
                <Button
                  variant="outline-primary"
                  onClick={handleAddAnotherConfiguration}
                  className="pv-ui-btn"
                  size="sm"
                >
                  <TbPlus /> Add New Configuration
                </Button>
              </div>

              <div className="configurations-list">
                {configurations.map((config, index) => (
                  <ConfigurationRecord
                    key={config.id}
                    config={config}
                    windows={config.windows}
                    onDelete={() => handleDeleteConfiguration(index)}
                  />
                ))}
              </div>

              <ExecutionActions align="center">
                <Button
                  variant="success"
                  onClick={handleSubmitAll}
                  disabled={ui.isSubmitting || configurations.length === 0}
                  className="pv-ui-submit-btn"
                >
                  {ui.isSubmitting ? "Submitting..." : `Submit `}
                </Button>
              </ExecutionActions>
            </div>
          )}
        </ExecutionContent>

        <Modal
          show={showDeleteConfirmation}
          onHide={cancelDeleteConfiguration}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirm Deletion</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete this configuration? This action
            cannot be undone.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={cancelDeleteConfiguration}>
              No, Cancel
            </Button>
            <Button variant="danger" onClick={confirmDeleteConfiguration}>
              Yes, Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </ExecutionModal>

      {isWindowSelectionConfirmationBoxOpen && (
        <WindowSelectionConfirmationBox
          allWindows={allWindows}
          onConfirm={handleConfirmWindowSelection}
        />
      )}
    </>
  );
};

export default SensorAnalysisManager;
